/**
 * Constantes de gameplay partagées entre le client et le serveur.
 * Source unique de vérité : ne jamais dupliquer ces valeurs ailleurs.
 */
/** Résolution (px) du personnage joueur. Fixe : la silhouette ne change pas.
 * Plus la valeur est haute, plus la peinture est détaillée (au prix de la bande
 * passante de présence : l'empreinte fait CHARACTER_SIZE²·4 octets). */
export const CHARACTER_SIZE = 96;
/** Nombre de rotations autorisées (par pas de 90°). */
export const CHARACTER_ROTATIONS = [0, 90, 180, 270];
/** Durées de phase par défaut (secondes). Surchargées par les réglages de salon. */
export const PHASE_DURATIONS = {
    camouflage: 40,
    seeking: 90,
    results: 12,
};
/** Bornes (secondes) que l'hôte peut choisir pour chaque phase réglable. */
export const PHASE_BOUNDS = {
    camouflage: { min: 15, max: 180 },
    seeking: { min: 30, max: 300 },
};
/** Bornes des autres réglages configurables dans le lobby. */
export const BOARD_BOUNDS = { min: 480, max: 1200 };
/** Zoom maximal autorisé au chercheur (plafond réglable). */
export const ZOOM_MAX_BOUNDS = { min: 2, max: 6 };
/** Délai (s) entre deux paliers de zoom quand le zoom progressif est actif. */
export const ZOOM_STEP_BOUNDS = { min: 5, max: 60 };
/** Cooldown (ms) après un clic raté du chercheur, anti-spam. */
export const WRONG_CLICK_COOLDOWN_MS = 3000;
/**
 * Tolérance (px, espace tableau) autour de la silhouette peinte pour valider un
 * clic. Le clic doit tomber sur un pixel opaque du personnage (hitbox au pixel
 * près), à cette marge près — plus de « touché de loin » sur la boîte englobante.
 */
export const HIT_TOLERANCE = 3;
/** Barème de points. */
export const SCORING = {
    /** Chercheur : par joueur trouvé. */
    seekerPerFind: 100,
    /** Chercheur : bonus s'il trouve tout le monde. */
    seekerSweepBonus: 20,
    /** Caché : points par tranche de survie. */
    hiddenSurvivalPoints: 5,
    hiddenSurvivalIntervalSec: 10,
    /** Caché : bonus si jamais trouvé. */
    hiddenNeverFoundBonus: 50,
    /** Caché : bonus de points proportionnel à la qualité du camouflage (score 0-100 → 0-max). */
    hiddenCamouflageBonusMax: 30,
    /** Seuil de camouflage (%) donnant un bonus XP. */
    camouflageBonusThreshold: 95,
    camouflageBonusXp: 40,
    /** XP de partie (#20). */
    matchXpBase: 20,
    matchXpPerFind: 6,
    matchXpPerSurvivalMinute: 8,
    matchXpWin: 30,
};
/** Contraintes joueurs par partie. */
export const LOBBY = {
    minPlayers: 2,
    maxPlayers: 16,
    codeLength: 6,
    /** Caractères utilisés pour les codes de salon (sans I/O/0/1 ambigus). */
    codeAlphabet: 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789',
};
/** Progression : XP cumulée nécessaire pour atteindre `level` (niveau 1 = 0 XP requis). */
export const xpForLevel = (level) => level <= 1 ? 0 : Math.floor(100 * Math.pow(level - 1, 1.6));
/** Niveau atteint pour un total d'XP (>= 1). */
export const levelForXp = (xp) => {
    let level = 1;
    while (xp >= xpForLevel(level + 1))
        level++;
    return level;
};
/** XP restante et seuil vers le niveau suivant (pour une barre de progression). */
export function xpProgress(xp) {
    const level = levelForXp(xp);
    const base = xpForLevel(level);
    const next = xpForLevel(level + 1);
    return { level, inLevel: xp - base, span: next - base };
}
/** Modes de jeu. */
export const GAME_MODES = ['classic', 'everyone-seeks', 'coop', 'blitz', 'ranked'];
export const MODE_META = {
    classic: {
        label: 'Classique',
        blurb: 'Chacun devient chercheur à son tour. Une manche par joueur.',
        implemented: true,
        durations: { camouflageSec: PHASE_DURATIONS.camouflage, seekingSec: PHASE_DURATIONS.seeking },
        maxRounds: null,
    },
    blitz: {
        label: 'Blitz',
        blurb: 'Manches éclair : peinture et traque accélérées, partie en 3 manches max.',
        implemented: true,
        durations: { camouflageSec: 20, seekingSec: 45 },
        maxRounds: 3,
    },
    'everyone-seeks': {
        label: 'Tout le monde cherche',
        blurb: 'Bientôt : aucun chercheur désigné, tout le monde traque en même temps.',
        implemented: false,
        durations: { camouflageSec: PHASE_DURATIONS.camouflage, seekingSec: PHASE_DURATIONS.seeking },
        maxRounds: null,
    },
    coop: {
        label: 'Coopératif',
        blurb: 'Bientôt : les cachés marquent des points ensemble contre le chrono.',
        implemented: false,
        durations: { camouflageSec: PHASE_DURATIONS.camouflage, seekingSec: PHASE_DURATIONS.seeking },
        maxRounds: null,
    },
    ranked: {
        label: 'Classé',
        blurb: 'Bientôt : parties classées avec calcul de rating (ELO).',
        implemented: false,
        durations: { camouflageSec: PHASE_DURATIONS.camouflage, seekingSec: PHASE_DURATIONS.seeking },
        maxRounds: null,
    },
};
/** Modes réellement jouables (pour l'UI et la validation serveur). */
export const PLAYABLE_MODES = GAME_MODES.filter((m) => MODE_META[m].implemented);
/** Visibilité d'un salon : public (listé, rejoignable sans code) ou privé (par code). */
export const ROOM_VISIBILITIES = ['public', 'private'];
/** Difficulté d'un tableau (1 à 4 étoiles). */
export const DIFFICULTY = [1, 2, 3, 4];
//# sourceMappingURL=constants.js.map